import React, { useEffect, useState } from "react";
import { ProDeliveryDelay } from "@/components/ProDeliveryDelay";
import { feeLabel } from "@/lib/proApi";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, MapPin, Truck, CheckCircle2, CreditCard, Landmark, Store } from "lucide-react";
import { proApi, errMsg } from "@/lib/proApi";
import { usePro } from "@/context/ProContext";
import { MondialRelayPicker } from "@/components/MondialRelayPicker";
import { Totals } from "@/pages/pro/ProCart";
import { AddressFields } from "@/pages/pro/ProAuth";
import { PAYPAL_OPTIONS, PayIn4 } from "@/components/PayIn4";

const Step = ({ n, title, children }) => (
  <section className="rounded-2xl bg-white border border-[var(--oz-border)] p-5 sm:p-6">
    <h2 className="font-semibold text-[var(--oz-text)] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[var(--oz-dark)] text-white text-xs flex items-center justify-center">{n}</span>{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const fmtAddr = (a) => a ? `${a.address}, ${a.postal_code} ${a.city} (${a.country})` : "";

export default function ProCheckout() {
  const { user, cart, config, loadCart } = usePro();
  const nav = useNavigate();
  const [qs] = useSearchParams();
  const [method, setMethod] = useState(qs.get("ship") === "carrier" ? "carrier" : "mondial_relay");
  const [relay, setRelay] = useState(null);
  const [addr, setAddr] = useState(user.shipping);
  const [quote, setQuote] = useState(null);
  const [cgv, setCgv] = useState(false);
  const [pay, setPay] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    const pc = method === "carrier" && addr?.postal_code ? `&postal_code=${encodeURIComponent(addr.postal_code)}&country=${addr.country || "FR"}` : "";
    const t = setTimeout(() => proApi.get(`/pro/cart?shipping_method=${method}${pc}`).then(({ data }) => { if (live) setQuote(data); }), 300);
    return () => { live = false; clearTimeout(t); };
  }, [method, cart, addr?.postal_code, addr?.country]);
  useEffect(() => { if (quote && !quote.min_delivery_ok && method !== "pickup") setMethod("pickup"); }, [quote, method]);

  if (!cart || !quote) return <div className="py-24" />;
  if (!cart.lines.length) return <div className="py-24 text-center"><p>Votre panier est vide.</p><Link to="/espace-pro" className="text-[var(--oz-gold)] font-semibold">Catalogue</Link></div>;

  const addrOk = addr && addr.address && addr.postal_code && addr.city;
  const noDelivery = !quote.min_delivery_ok;
  const blocker = noDelivery && method !== "pickup" ? config?.min_delivery_msg
    : method === "mondial_relay" && !relay ? "Choisissez un point relais Mondial Relay."
    : method === "carrier" && !addrOk ? "Renseignez l'adresse de livraison."
    : !cgv ? "Veuillez accepter les CGV." : "";
  const payload = () => ({ shipping_method: method, relay_point: method === "mondial_relay" ? relay : null,
    shipping_address: method === "carrier" ? addr : null, cgv_accepted: cgv, origin_url: window.location.origin });

  const payTransfer = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await proApi.post("/pro/checkout/transfer", payload());
      await loadCart(user);
      nav(`/espace-pro/commande/succes?order=${data.order_number}&transfer=1`, { state: { order: data } });
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  const payStripe = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await proApi.post("/pro/checkout/stripe", payload());
      window.location.href = data.checkout_url;
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  const b = user.billing;
  return (
    <div data-testid="pro-checkout" className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <h1 className="font-serif text-4xl sm:text-5xl text-[var(--oz-text)]">Validation de la commande</h1>
      <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        <div className="space-y-4">
          {quote.quote_number && <p data-testid="pro-checkout-quote" className="rounded-xl bg-[#FBF6EC] border border-[var(--oz-gold)] px-4 py-3 text-sm">Commande selon devis <strong>{quote.quote_number}</strong> : prix HT du devis appliqués.</p>}
          <Step n={1} title="Vérification du panier">
            <ul className="text-sm divide-y divide-[var(--oz-border)]">
              {quote.lines.map((l) => (
                <li key={`${l.slug}-${l.pack_size}`} className="py-2 flex justify-between gap-3">
                  <span>{l.name} — {l.lots > 1 ? `${l.lots} × ${l.pack_size} = ` : ""}{l.pieces} pièces</span>
                  <span className="font-semibold whitespace-nowrap">{(l.total_ht_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })} HT</span>
                </li>
              ))}
            </ul>
            <Link to="/espace-pro/panier" className="text-xs underline text-[var(--oz-muted)]">Modifier le panier</Link>
          </Step>
          <Step n={2} title="Coordonnées professionnelles et facturation">
            <div data-testid="pro-checkout-billing" className="text-sm text-[var(--oz-muted)] space-y-0.5">
              <p className="font-semibold text-[var(--oz-text)]">{user.company_name}</p>
              <p>{user.first_name} {user.last_name} — {user.email} — {user.phone}</p>
              {user.siret && <p>SIRET {user.siret}</p>}
              {user.vat_number && <p>TVA {user.vat_number}</p>}
              <p>Facturation : {fmtAddr(b)}</p>
            </div>
            <Link to="/espace-pro/compte?tab=profil" data-testid="pro-checkout-edit-profile" className="text-xs underline text-[var(--oz-muted)]">Modifier mes coordonnées</Link>
          </Step>
          <Step n={3} title="Mode de livraison">
            <div className="grid sm:grid-cols-2 gap-3">
              {[["mondial_relay", MapPin, "Mondial Relay", `Point relais — ${config?.mondial_relay_fee_ht ? config.mondial_relay_fee_ht + " € HT" : "gratuit"}`],
                ["carrier", Truck, "Livraison par transporteur", `${config?.carrier_fee_ht ? `${feeLabel(config.carrier_fee_ht)} par commande` : "Gratuit en métropole"} · DOM-TOM 30 à 60 € · Europe 50 € HT`],
                ["pickup", Store, "Retrait au dépôt", config?.pickup_address || "Chatou (78)"]].map(([k, Icon, t, d]) => (
                <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`pro-ship-${k}`} disabled={noDelivery && k !== "pickup"}
                  className={`text-left rounded-xl border p-4 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${method === k ? "border-[var(--oz-dark)] ring-1 ring-[var(--oz-dark)]" : "border-[var(--oz-border)]"}`}>
                  <Icon className="w-5 h-5 text-[var(--oz-gold)]" /><p className="mt-2 font-semibold text-sm">{t}</p><p className="text-xs text-[var(--oz-muted)]">{d}</p>
                </button>
              ))}
            </div>
            {config?.min_delivery_msg && <p data-testid="pro-checkout-min-delivery" className={`mt-3 text-xs ${noDelivery ? "text-[var(--oz-badge)] font-semibold" : "text-[var(--oz-muted)]"}`}>{config.min_delivery_msg}</p>}
            <ProDeliveryDelay className="mt-3" testid="pro-checkout-delay" />
            {method === "mondial_relay" && (
              <div className="mt-4">
                {relay && <p data-testid="pro-relay-selected" className="mb-3 text-sm flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-700 flex-shrink-0" /> {relay.name} — {relay.address}</p>}
                <div className="relative z-0 isolate"><MondialRelayPicker onSelect={setRelay} /></div>
              </div>
            )}
            {method === "carrier" && <div className="mt-4"><AddressFields prefix="pro-ship-addr" value={addr} onChange={setAddr} />
              {config?.dom_tom_fees && <p data-testid="pro-checkout-dom-fees" className="mt-2 text-xs text-[var(--oz-muted)]">{config.dom_tom_fees}. {config.belgium_fees}. Calcul automatique selon le pays et le code postal (DOM-TOM : 971 à 976, 984 à 988).</p>}</div>}
          </Step>
          <Step n={4} title="Conditions et paiement">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} data-testid="pro-cgv-checkbox" className="mt-0.5 w-4 h-4 accent-[var(--oz-gold)]" />
              <span>J'ai lu et j'accepte les <Link to="/legal/cgv" target="_blank" className="underline font-semibold">Conditions Générales de Vente</Link> *</span>
            </label>
            {(config?.paypal_enabled || config?.transfer_enabled) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {[["stripe", "Carte bancaire / Klarna"], config?.paypal_enabled && ["paypal", "PayPal / 4X"], config?.transfer_enabled && ["transfer", "Virement bancaire"]].filter(Boolean).map(([k, t]) => (
                  <button key={k} onClick={() => setPay(k)} data-testid={`pro-pay-${k}`} className={`px-4 py-2 rounded-full border text-sm font-semibold ${pay === k ? "bg-[var(--oz-dark)] text-white border-[var(--oz-dark)]" : "border-[var(--oz-border)]"}`}>{t}</button>
                ))}
              </div>
            )}
          </Step>
        </div>
        <aside className="rounded-2xl bg-white border border-[var(--oz-border)] p-6 lg:sticky lg:top-36 space-y-4">
          <p className="font-semibold">Récapitulatif</p>
          <Totals q={quote} shippingLabel={{ carrier: "transporteur", pickup: "retrait au dépôt" }[method] || "Mondial Relay"} />
          {blocker && <p data-testid="pro-checkout-blocker" className="text-xs text-[var(--oz-badge)]">{blocker}</p>}
          {err && <p data-testid="pro-checkout-error" className="text-sm text-[var(--oz-badge)]">{err}</p>}
          {pay === "transfer" ? (
            <>
              <button onClick={payTransfer} disabled={!!blocker || busy} data-testid="pro-transfer-submit"
                className="w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4" />} Valider la commande
              </button>
              <p className="text-[11px] text-[var(--oz-muted)]">Vous recevrez nos coordonnées bancaires. La commande est préparée et facturée à réception du virement ({config.transfer_due_days} jours maximum).</p>
            </>
          ) : pay === "stripe" || !config?.paypal_enabled ? (
            <button onClick={payStripe} disabled={!!blocker || busy} data-testid="pro-pay-submit"
              className="w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Payer par carte / Klarna
            </button>
          ) : blocker ? null : (<>
            <PayIn4 amount={quote.total_ttc_cents / 100} testid="pro-checkout-pay4" />
            <PayPalScriptProvider options={PAYPAL_OPTIONS(config.paypal_client_id)}>
              <PayPalButtons style={{ layout: "vertical", label: "pay" }} forceReRender={[quote.total_ttc_cents, method, relay?.id]}
                createOrder={async () => { setErr(""); try { const { data } = await proApi.post("/pro/checkout/paypal/create", payload()); return data.id; } catch (e) { setErr(errMsg(e)); throw e; } }}
                onApprove={async (d) => {
                  try {
                    const { data } = await proApi.post(`/pro/checkout/paypal/capture/${d.orderID}`);
                    if (data.status === "paid") { await loadCart(user); nav(`/espace-pro/commande/succes?order=${data.order_number}`); }
                    else setErr("Le paiement PayPal n'a pas pu être confirmé.");
                  } catch (e) { setErr(errMsg(e)); }
                }} />
            </PayPalScriptProvider></>
          )}
          {pay !== "transfer" && <p className="text-[11px] text-[var(--oz-muted)]">Paiement sécurisé : vos données bancaires sont traitées par Stripe ou PayPal et ne sont jamais stockées par Ozentya.</p>}
        </aside>
      </div>
    </div>
  );
}

export const BankBox = ({ o }) => (
  <div data-testid="pro-bank-box" className="mt-5 text-left rounded-2xl border border-[var(--oz-border)] bg-white p-5 text-sm space-y-1">
    {[["Titulaire", o.bank.holder], ["IBAN", o.bank.iban], ["BIC", o.bank.bic], ["Banque", o.bank.bank_name],
      ["Montant", (o.total_ttc_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })], ["Référence", o.order_number],
      ["À payer avant le", o.due_at ? new Date(o.due_at).toLocaleDateString("fr-FR") : ""]].filter(([, v]) => v).map(([k, v]) => (
      <p key={k} className="flex justify-between gap-3"><span className="text-[var(--oz-muted)]">{k}</span><strong className="text-right break-all">{v}</strong></p>
    ))}
  </div>
);

export function ProSuccess() {
  const [sp] = useSearchParams();
  const loc = useLocation();
  const { user, loadCart } = usePro();
  const [tOrder, setTOrder] = useState(loc.state?.order || null);
  useEffect(() => {
    if (!sp.get("transfer") || tOrder) return;
    proApi.get("/pro/orders").then(({ data }) => setTOrder(data.find((o) => o.order_number === sp.get("order")) || null)).catch(() => {});
  }, [sp, tOrder]);
  const [state, setState] = useState({ status: sp.get("order") ? "paid" : "checking", order: sp.get("order") });
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await proApi.get(`/pro/checkout/stripe/status/${sid}`);
        if (data.status !== "pending_payment") { setState({ status: data.status, order: data.order_number }); loadCart(user); return; }
      } catch { /* retry */ }
      if (++n < 10) setTimeout(poll, 2000); else setState((s) => ({ ...s, status: "pending" }));
    };
    poll();
  }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="pro-success" className="max-w-xl mx-auto px-4 py-20 text-center">
      {sp.get("transfer") ? (
        <>
          <Landmark className="w-12 h-12 mx-auto text-[var(--oz-gold)]" />
          <h1 className="mt-4 font-serif text-4xl">Commande enregistrée</h1>
          <p className="mt-3 text-[var(--oz-muted)]">Commande <strong data-testid="pro-success-order">{sp.get("order")}</strong> en attente de votre virement. Les coordonnées bancaires vous ont été envoyées par e-mail.</p>
          {tOrder?.bank && <BankBox o={tOrder} />}
          <Link to="/espace-pro/compte" data-testid="pro-success-account" className="mt-6 inline-block px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold">Voir mes commandes</Link>
        </>
      ) : state.status === "checking" ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-[var(--oz-gold)]" /> : state.status === "pending" ? (
        <p data-testid="pro-success-pending">Paiement en cours de confirmation. Vous recevrez un e-mail dès validation.</p>
      ) : (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-green-700" />
          <h1 className="mt-4 font-serif text-4xl">Commande confirmée</h1>
          <p className="mt-3 text-[var(--oz-muted)]">Commande <strong data-testid="pro-success-order">{state.order}</strong>. Un e-mail de confirmation avec votre facture vient de vous être envoyé.</p>
          <Link to="/espace-pro/compte" data-testid="pro-success-account" className="mt-6 inline-block px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold">Voir mes commandes et factures</Link>
        </>
      )}
    </div>
  );
}
